import { ComponentProps, ReactNode, useEffect, useState } from 'react';
import { darkModeAware } from '@glu/theming';

import classnames from 'classnames';
import CheckboxRadio from '../CheckboxRadio/CheckboxRadio';
import LabelText, { ILabelTextProps } from '../LabelText/LabelText';
import useStyles from './CheckboxRadioGroup.styles';

export interface ICheckboxRadioGroupProps
  extends Pick<ILabelTextProps, 'optional' | 'tooltipContent'> {
  children?: ReactNode;
  /** Classname to apply to the field set */
  className?: string;
  /** Determines if dark styling should be applied */
  dark?: boolean;
  /** defines if group items should be formatted horizontal (inline) or vertical (block) */
  format?: 'block' | 'inline';
  /** Label text for group */
  groupLabel?: ILabelTextProps['label'];
  /** Class name to apply to group label legend element  */
  groupLabelClass?: string;
  /** List of items to appear in group */
  itemList?: ComponentProps<typeof CheckboxRadio>[];
  /** defines if this is a list of checkboxes or a radio group */
  type?: 'checkbox' | 'radio';
}

const setDefaultCheckedFalse = (itemList: ICheckboxRadioGroupProps['itemList']) =>
  itemList?.map((item) => ({
    checked: false,
    ...item,
  }));

const CheckboxRadioGroup = ({
  children,
  className,
  dark,
  format = 'inline',
  groupLabel,
  groupLabelClass,
  itemList,
  optional,
  tooltipContent,
  type = 'checkbox',
}: ICheckboxRadioGroupProps) => {
  const [statefulItemList, setStatefulItemList] = useState(
    itemList && setDefaultCheckedFalse(itemList)
  );
  const classes = useStyles();

  /*
   * If itemList changes, make sure to update the statefulItemList
   */
  useEffect(() => {
    if (itemList) {
      setStatefulItemList(setDefaultCheckedFalse(itemList));
    }
  }, [itemList]);

  const handleChange = (
    statefulName: string,
    name: string,
    value: string,
    itemCb?: (name: string, value: string) => void
  ) => {
    const updatedList =
      statefulItemList &&
      statefulItemList.map((item, idx) => {
        if (statefulName === `${item.name}-${idx}`) {
          return {
            ...item,
            checked: type === 'radio' ? true : !!value,
          };
        }
        return type === 'radio' ? { ...item, checked: false } : item;
      });
    setStatefulItemList(updatedList);
    itemCb?.(name, value);
  };

  return (
    <fieldset className={classnames(classes.root, className)}>
      {groupLabel && (
        <legend className={classnames(classes.group, groupLabelClass)}>
          <LabelText
            className={classes.optionalText}
            label={groupLabel}
            optional={optional}
            tooltipContent={tooltipContent}
          />
        </legend>
      )}
      {children ||
        statefulItemList?.map((radioProps, idx) => {
          const statefulName = `${radioProps.name}-${idx}`;
          return (
            <div
              key={`CheckboxRadioGroup_item_${radioProps.htmlId}`}
              className={format === 'inline' ? classes.inline : classes.block}
            >
              <CheckboxRadio
                key={`CheckboxRadioGroup_item_${radioProps.htmlId}`}
                className={classes.inlineBlock}
                dark={dark}
                type={type}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...radioProps}
                onChange={(name: string, value: string) =>
                  handleChange(statefulName, name, value, radioProps.onChange)
                }
              />
            </div>
          );
        })}
    </fieldset>
  );
};

export const BareCheckboxRadioGroup = CheckboxRadioGroup;
export default darkModeAware(CheckboxRadioGroup);
